import { useState } from 'react';
import { Badge, Button, Card, ConfirmDialog, EmptyState, LoadingList, SectionHeader } from '@timely/ui';
import { holds, type TemplateChannel } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { TemplateDialog } from '../templates/TemplateDialog';
import {
    archiveTemplate, deleteTemplate, duplicateTemplate, setDefaultTemplate, useTemplates,
    type MessageTemplate,
} from '../templates/useTemplates';

const CHANNEL_LABEL: Record<TemplateChannel, string> = {
    whatsapp: 'WhatsApp', email: 'Email', internal: 'Internal note',
};

/**
 * Message templates.
 *
 * Read by anyone on the team — using a template a manager wrote is not the
 * same right as writing one — and managed only by `templates.manage`, the
 * same capability the data source and company settings screens use for
 * anything that shapes what other people see or send.
 *
 * A brand-new company starts with none. That is deliberate: a default
 * WhatsApp reminder written for a different company's customers is not a
 * safer starting point than a blank page and a clear next action.
 */
export function Templates() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canManage = holds(context, 'templates.manage');

    const { templates, loading, error, reload } = useTemplates(workspace.id);
    const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<MessageTemplate | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    const active = templates.filter(t => !t.archivedAt);
    const archived = templates.filter(t => t.archivedAt);

    const act = async (label: string, run: () => Promise<void>) => {
        setNotice(null);
        try {
            await run();
            await reload();
            setNotice({ tone: 'ok', text: label });
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'That did not work.' });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Page>
            <SectionHeader
                title="Templates"
                subtitle="The wording your team sends, kept in one place and never crossing into another company."
                actions={canManage
                    ? <Button variant="primary" onClick={() => setEditing('new')}>New template</Button>
                    : undefined}
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading your templates" rows={4} />
                ) : active.length === 0 ? (
                    <EmptyState
                        title="No templates yet"
                        hint={canManage
                            ? 'Write the wording your team sends for a reminder, and everyone on the team can use it.'
                            : 'Once somebody sets one up, it appears here.'}
                        action={canManage
                            ? <Button variant="primary" onClick={() => setEditing('new')}>Write your first template</Button>
                            : undefined}
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {active.map(t => (
                            <li key={t.id} className="px-5 py-3 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-semibold text-label">{t.name}</p>
                                        <Badge tone="neutral">{CHANNEL_LABEL[t.channel]}</Badge>
                                        {t.isDefault && <Badge tone="brand">Default</Badge>}
                                    </div>
                                    <p className="text-[13px] text-label-3 mt-1 line-clamp-2 whitespace-pre-wrap">{t.content}</p>
                                </div>
                                {canManage && (
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <Button variant="secondary" size="sm" onClick={() => setEditing(t)}>Edit</Button>
                                        <Button
                                            variant="secondary" size="sm" disabled={busyId === t.id}
                                            onClick={() => { setBusyId(t.id); void act('Duplicated.', async () => { await duplicateTemplate(workspace.id, t); }); }}
                                        >Duplicate</Button>
                                        {!t.isDefault && (
                                            <Button
                                                variant="secondary" size="sm" disabled={busyId === t.id}
                                                onClick={() => { setBusyId(t.id); void act('Set as default.', async () => { await setDefaultTemplate(workspace.id, t.id); }); }}
                                            >Make default</Button>
                                        )}
                                        <Button
                                            variant="secondary" size="sm" disabled={busyId === t.id}
                                            onClick={() => { setBusyId(t.id); void act('Archived.', async () => { await archiveTemplate(t.id, true); }); }}
                                        >Archive</Button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {canManage && archived.length > 0 && (
                <Card className="mt-5 overflow-hidden">
                    <div className="px-5 py-3 border-b border-separator">
                        <p className="text-[13px] font-semibold text-label-3">Archived — kept, not offered</p>
                    </div>
                    <ul className="divide-y divide-separator">
                        {archived.map(t => (
                            <li key={t.id} className="px-5 py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-semibold text-label-2">{t.name}</p>
                                    <Badge tone="neutral">{CHANNEL_LABEL[t.channel]}</Badge>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Button
                                        variant="secondary" size="sm" disabled={busyId === t.id}
                                        onClick={() => { setBusyId(t.id); void act('Restored.', async () => { await archiveTemplate(t.id, false); }); }}
                                    >Restore</Button>
                                    <Button
                                        variant="secondary" size="sm" disabled={busyId === t.id}
                                        onClick={() => setConfirmDelete(t)}
                                    >Delete</Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {editing && (
                <TemplateDialog
                    workspaceId={workspace.id}
                    template={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async message => {
                        setEditing(null);
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}

            {confirmDelete && (
                <ConfirmDialog
                    open
                    title="Delete this template?"
                    confirmLabel="Delete"
                    tone="danger"
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        const t = confirmDelete;
                        setConfirmDelete(null);
                        await act('Deleted.', async () => { await deleteTemplate(t.id); });
                    }}
                >
                    "{confirmDelete.name}" will be gone for good. Nothing else refers to it.
                </ConfirmDialog>
            )}
        </Page>
    );
}
